'use client';

import { useFormContext } from 'react-hook-form';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { AccountCard } from '@eliadmin/core/src/components/nt/account/shell/account-card';
import {
  fieldId,
  ProfileCountryField,
  ProfileTextField,
} from '@eliadmin/core/src/components/nt/account/profile/profile-fields';
import type { ProfileFormInput } from '@eliadmin/core/src/components/nt/account/profile/profile-schema';
import { Checkbox } from '@eliadmin/core/src/components/nt/forms/controls';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { CheckoutTexts } from '@eliadmin/core/src/utils/nt/checkout';

type AddressNames = {
  street: 'address' | 'delivery_address';
  city: 'city' | 'delivery_city';
  postcode: 'postcode' | 'delivery_postcode';
  country: 'country' | 'delivery_country';
};

const BILLING: AddressNames = {
  street: 'address',
  city: 'city',
  postcode: 'postcode',
  country: 'country',
};

const DELIVERY: AddressNames = {
  street: 'delivery_address',
  city: 'delivery_city',
  postcode: 'delivery_postcode',
  country: 'delivery_country',
};

/**
 * Figma rows of „Card · Fakturačná adresa“ (205:11129): „Ulica a číslo domu“, then „Mesto“ |
 * „PSČ“ (724 + 200 px at 1728; the postcode keeps a narrow column on phones too), then „Krajina“.
 */
function AddressRows({
  names,
  countries,
  section,
  streetLabel,
}: {
  names: AddressNames;
  countries: Country[];
  section: 'billing' | 'shipping';
  streetLabel: string;
}) {
  const { t } = useTranslations();

  return (
    <>
      <ProfileTextField
        name={names.street}
        label={streetLabel}
        required
        autoComplete={`${section} street-address`}
      />
      <div className="grid grid-cols-[minmax(0,1fr)_7.5rem] items-start gap-4 @[36rem]:grid-cols-[minmax(0,1fr)_200px]">
        <ProfileTextField
          name={names.city}
          label={t('text39')}
          required
          autoComplete={`${section} address-level2`}
        />
        <ProfileTextField
          name={names.postcode}
          label={t('text40')}
          required
          autoComplete={`${section} postal-code`}
        />
      </div>
      <ProfileCountryField
        name={names.country}
        label={t('text519')}
        countries={countries}
        autoComplete={`${section} country`}
      />
    </>
  );
}

/**
 * Figma „Card · Fakturačná adresa“ (205:11129) with „Doručovacia adresa je rovnaká ako
 * fakturačná“; a company customer's billing address is the seat („Fakturačná adresa (sídlo
 * firmy)“ of the checkout).
 */
export function BillingCard({
  countries,
  isCompany,
  checkoutTexts,
}: {
  countries: Country[];
  isCompany: boolean;
  checkoutTexts: CheckoutTexts;
}) {
  const { t } = useTranslations();
  const texts = useAccountTexts();
  const { register } = useFormContext<ProfileFormInput>();

  return (
    <AccountCard title={(isCompany && checkoutTexts.seat) || t('text45')}>
      <AddressRows
        names={BILLING}
        countries={countries}
        section="billing"
        streetLabel={checkoutTexts.street}
      />
      <Checkbox
        id={fieldId('same_delivery')}
        label={texts.sameAddress}
        {...register('same_delivery')}
      />
    </AccountCard>
  );
}

/**
 * „Doručovacia adresa“ while it differs from the billing one – not drawn in the design (the box is
 * ticked there); the same card and rows as the billing address.
 */
export function DeliveryCard({
  countries,
  checkoutTexts,
}: {
  countries: Country[];
  checkoutTexts: CheckoutTexts;
}) {
  const { t } = useTranslations();

  return (
    <AccountCard title={t('text2139')}>
      <AddressRows
        names={DELIVERY}
        countries={countries}
        section="shipping"
        streetLabel={checkoutTexts.street}
      />
    </AccountCard>
  );
}
